class CartDrawer extends DrawerComponent {
  constructor() {
    super();
    window.FoxKitSections = FoxTheme.utils.getSectionId(this);

    this.onCartRefreshListener = this.onCartRefresh.bind(this);
    this.getSectionToRenderListener = this.getSectionToRender.bind(this);

    // Check for open_cart URL parameter and auto-open drawer
    this.checkOpenCartParam();
  }

  checkOpenCartParam() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('open_cart') === '1') {
      // Remove the parameter from URL without reload
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('open_cart');
      window.history.replaceState({}, '', newUrl);

      // Open the cart drawer after a small delay to ensure DOM is ready
      requestAnimationFrame(() => {
        this.show();
      });
    }
  }

  get requiresBodyAppended() {
    return false;
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('cart:grouped-sections', this.getSectionToRenderListener);
    document.addEventListener('cart:refresh', this.onCartRefreshListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:grouped-sections', this.getSectionToRenderListener);
    document.removeEventListener('cart:refresh', this.onCartRefreshListener);
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }

  show(focusElement = null, animate = true) {
    super.show(focusElement, animate);

    if (this.open) {
      FoxTheme.a11y.trapFocus(this, this.focusElement);
    }
  }

  async onCartRefresh(event) {
    const cartId = `CartDrawer-${this.sectionId}`;

    const cartElement = document.getElementById(cartId);
    if (!cartElement) return;

    try {
      const response = await fetch(`${FoxTheme.routes.root_url}?section_id=${this.sectionId}`);
      const responseText = await response.text();

      const parser = new DOMParser();
      const parsedHTML = parser.parseFromString(responseText, 'text/html');

      const newCartContent = parsedHTML.getElementById(cartId).innerHTML;
      cartElement.innerHTML = newCartContent;

      if (event.detail && event.detail.open === true) {
        this.show();
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }
}
customElements.define('cart-drawer', CartDrawer);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', (event) => {
      if (event.target && event.target.closest('quantity-input')) {
        FoxTheme.utils.debounce(this.onChange(event), 300);
      }
    });
    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdate.bind(this)
    );

    this.cartItemProducts = this.querySelectorAll('.cart-item__product');
    this.cartItemQuantitys = this.querySelectorAll('.cart-item__quantity');

    const loadTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const template = element.querySelector('template');
        const templateContent = template && document.importNode(template.content, true);
        const parent = element.querySelector(parentSelector);

        if (parent && template && !parent.querySelector('.template-content')) {
          const contentWrapper = document.createElement('div');
          contentWrapper.classList.add('template-content', 'w-full');
          contentWrapper.appendChild(templateContent);
          parent.appendChild(contentWrapper);
        }
      });
    };

    const removeTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const parent = element.querySelector(parentSelector);
        const templateContent = parent?.querySelector('.template-content');

        if (templateContent) {
          parent.removeChild(templateContent);
        }
      });
    };

    const handleTabletMatch = () => {
      loadTemplateContent(this.cartItemProducts, '.cart-item__product--info');
      removeTemplateContent(this.cartItemQuantitys, '.cart-item__quantity-wrapper');
    };

    const handleTabletUnmatch = () => {
      removeTemplateContent(this.cartItemProducts, '.cart-item__product--info');
      loadTemplateContent(this.cartItemQuantitys, '.cart-item__quantity-wrapper');
    };

    const mqlTablet = window.matchMedia(FoxTheme.config.mediaQueryTablet);
    FoxTheme.config.mqlTablet = mqlTablet.matches;

    if (FoxTheme.config.mqlTablet) {
      handleTabletMatch();
    } else {
      handleTabletUnmatch();
    }

    mqlTablet.onchange = (event) => {
      if (event.matches) {
        handleTabletMatch();
      } else {
        FoxTheme.config.mqlTablet = false;
        handleTabletUnmatch();
      }
    };

    window.FoxKitSections = FoxTheme.utils.getSectionId(this);
  }

  cartUpdateUnsubscriber = undefined;

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  setValidity(target, message) {
    target.setCustomValidity(message);
    target.reportValidity();
    target.value = target.defaultValue;
    target.select();
  }

  validateQuantity(event) {
    const target = event.target;
    const inputValue = parseInt(target.value);
    const index = target.getAttribute('data-index');
    let message = '';

    if (inputValue < parseInt(target.getAttribute('data-min'))) {
      message = FoxTheme.quickOrderListStrings.min_error.replace('[min]', target.getAttribute('data-min'));
    } else if (inputValue > parseInt(target.max)) {
      message = FoxTheme.quickOrderListStrings.max_error.replace('[max]', target.max);
    } else if (inputValue % parseInt(target.step) !== 0) {
      message = FoxTheme.quickOrderListStrings.step_error.replace('[step]', target.step);
    }

    if (message) {
      this.setValidity(target, message);
    } else {
      target.setCustomValidity('');
      target.reportValidity();
      this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
    }
  }

  onCartUpdate(event) {
    if (event.cart.errors) {
      this.onCartError(event.cart.errors, event.target);
      return;
    }

    const sectionId = FoxTheme.utils.getSectionId(this);
    const sectionToRender = new DOMParser().parseFromString(event.cart.sections[sectionId], 'text/html');

    const cartDrawer = document.querySelector(`#CartDrawer-${sectionId}`);
    const cartDrawerBody = document.querySelector(`#CartDrawerBody-${sectionId}`);
    const cartDrawerFooter = document.querySelector(`#CartDrawerFooter-${sectionId}`);
    const cartDrawerEmpty = document.querySelector(`#CartDrawerEmpty-${sectionId}`);
    if (cartDrawer) {
      // const updatedElement = sectionToRender.querySelector(`#CartDrawer-${sectionId}`);
      // if (updatedElement) {
      //   cartDrawer.innerHTML = updatedElement.innerHTML;
      // }
      const cartDrawerBodyUpdate = sectionToRender.querySelector(`#CartDrawerBody-${sectionId}`);
      const cartDrawerFooterUpdate = sectionToRender.querySelector(`#CartDrawerFooter-${sectionId}`);
      const cartDrawerEmptyUpdate = sectionToRender.querySelector(`#CartDrawerEmpty-${sectionId}`);

      if (cartDrawerBodyUpdate) {
        cartDrawerBody.innerHTML = cartDrawerBodyUpdate.innerHTML;
      }
      if (cartDrawerFooterUpdate) {
        cartDrawerFooter.innerHTML = cartDrawerFooterUpdate.innerHTML;
      }

      if (cartDrawerEmptyUpdate) {
        cartDrawerEmpty.innerHTML = cartDrawerEmptyUpdate.innerHTML;
      }

      if (event.cart.item_count > 0) {
        cartDrawerBody.classList.remove('hidden');
        cartDrawerFooter.classList.remove('hidden');
        cartDrawerEmpty.classList.add('hidden');
      } else {
        cartDrawerBody.classList.add('hidden');
        cartDrawerFooter.classList.add('hidden');
        cartDrawerEmpty.classList.remove('hidden');
      }
    }

    const mainCart = document.querySelector(`#MainCart-${sectionId}`);
    if (mainCart) {
      const updatedElement = sectionToRender.querySelector(`#MainCart-${sectionId}`);
      if (updatedElement) {
        mainCart.innerHTML = updatedElement.innerHTML;
      } else {
        mainCart.closest('.cart').classList.add('is-empty');
        mainCart.remove();
      }
    }

    const lineItem =
      document.getElementById(`CartItem-${event.line}`) || document.getElementById(`CartDrawer-Item-${event.line}`);

    if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
      FoxTheme.a11y.trapFocus(mainCart || cartDrawer, lineItem.querySelector(`[name="${event.name}"]`));
    } else if (event.cart.item_count === 0) {
      cartDrawer
        ? FoxTheme.a11y.trapFocus(cartDrawer, cartDrawer.querySelector('a'))
        : FoxTheme.a11y.trapFocus(document.querySelector('.cart__empty'), document.querySelector('a'));
    } else {
      cartDrawer
        ? FoxTheme.a11y.trapFocus(cartDrawer, cartDrawer.querySelector('.cart-item__title'))
        : FoxTheme.a11y.trapFocus(mainCart, mainCart.querySelector('.cart-item__title'));
    }

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart: event.cart,
        },
      })
    );
  }

  onCartError(errors, target) {
    if (target) {
      this.hideLoader(target.getAttribute('data-index'));
      this.setValidity(target, errors);
      return;
    } else {
      window.location.href = FoxTheme.routes.cart_url;
    }

    alert(errors);
  }

  updateQuantity(line, quantity, name, target) {
    this.showLoader(line);

    const lineItemEl =
      document.getElementById(`CartDrawer-Item-${line}`) || document.getElementById(`CartItem-${line}`);
    const lineItemKey = lineItemEl?.dataset?.key;

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    // Use key if available (more stable), otherwise use line number
    const body = JSON.stringify(
      lineItemKey
        ? {
            id: lineItemKey,
            quantity: quantity,
            sections: sectionsToBundle,
          }
        : {
            line: line,
            quantity: quantity,
            sections: sectionsToBundle,
          }
    );

    fetch(`${FoxTheme.routes.cart_change_url}`, {
      ...FoxTheme.utils.fetchConfig(),
      ...{ body },
    })
      .then((response) => response.json())
      .then((state) => {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          sources: 'cart-items',
          cart: state,
          target,
          line,
          name,
        });
      })
      .catch((error) => {
        console.log(error);
      });
  }

  showLoader(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);
    const loaders = document.querySelectorAll(`#Loader-${sectionId}-${line}`);
    if (loaders) {
      loaders.forEach((loader) => {
        loader.classList.add('btn--loading');
      });
    }
  }

  hideLoader(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);
    const loaders = document.querySelectorAll(`#Loader-${sectionId}-${line}`);
    if (loaders) {
      loaders.forEach((loader) => {
        loader.classList.remove('btn--loading');
      });
    }
  }

  setValidity(target, message) {
    target.setCustomValidity(message);
    target.reportValidity();
    target.value = target.defaultValue;
    target.select();
  }
}

customElements.define('cart-items', CartItems);

class CartRemoveItem extends HTMLAnchorElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();

      const cartItems = this.closest('cart-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-remove-item', CartRemoveItem, { extends: 'a' });

class CartAddonModal extends ModalComponent {
  constructor() {
    super();
  }

  /**
   * To avoid lost focus input elements
   */
  get focusElement() {
    return this.querySelector('input, textarea, select') || this.querySelector('button');
  }
}
customElements.define('cart-addon-modal', CartAddonModal);

class CartAddonAccordion extends AccordionDetails {
  constructor() {
    super();
  }

  get focusElement() {
    return this.querySelector('input, textarea, select') || this.querySelector('button');
  }

  handleAfterAnimated() {
    if (this.open) FoxTheme.a11y.trapFocus(this, this.focusElement);
  }
}
customElements.define('cart-addon-accordion', CartAddonAccordion, { extends: 'details' });

class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', FoxTheme.utils.debounce(this.onChange.bind(this), 300));

    if (this.button && this.cartNoteDetailsSummary) {
      this.button.addEventListener('click', () => {
        this.cartNoteDetailsSummary.close();
      });
    }
  }

  get cartNoteDetailsSummary() {
    return this.closest('[is="accordion-details"]');
  }

  get button() {
    return this.querySelector('[type="button"]');
  }

  onChange(event) {
    const body = JSON.stringify({ note: event.target.value });
    fetch(`${FoxTheme.routes.cart_update_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } });
  }
}
customElements.define('cart-note', CartNote);
class CartDiscount extends HTMLFormElement {
  constructor() {
    super();

    this.addEventListener('submit', this.handleFormSubmit.bind(this));
  }

  get submitEl() {
    return (this._submitEl = this._submitEl || this.querySelector('[type="submit"]'));
  }

  get messageEl() {
    return (this._messageEl = this._messageEl || this.querySelector('.form__message'));
  }

  get couponEl() {
    return (this._couponEl = this._couponEl || this.querySelector('input[name="discount"]'));
  }

  get cartAddonDrawer() {
    return this.closest('.cart-addons-drawer');
  }

  get cartDiscountsEl() {
    return document.querySelector('.cart__discounts');
  }

  getDiscounts() {
    const discounts = [];

    if (this.cartDiscountsEl) {
      const items = this.cartDiscountsEl.querySelectorAll('.cart__discount');
      items &&
        items.forEach((item) => {
          discounts.push(item.dataset.discountCode);
        });
    }

    return discounts;
  }

  handleFormSubmit(event) {
    event.preventDefault();

    if (this.submitEl.getAttribute('aria-disabled') === 'true') return;

    this.displayFormErrors();

    const newDiscountCode = this.couponEl.value;
    const discounts = this.getDiscounts();

    if (discounts.includes(newDiscountCode)) {
      this.displayFormErrors(FoxTheme.cartStrings.duplicateDiscountError);
      return;
    }

    discounts.push(newDiscountCode);

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const config = FoxTheme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);
    formData.append('discount', discounts.join(','));

    config.body = formData;

    this.submitEl.setAttribute('aria-disabled', 'true');
    this.submitEl.classList.add('btn--loading');

    fetch(FoxTheme.routes.cart_update_url, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (
          parsedState.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === newDiscountCode && discount.applicable === false;
          })
        ) {
          this.couponEl.value = '';
          this.displayFormErrors(FoxTheme.cartStrings.applyDiscountError);
          return;
        }

        if (this.cartAddonDrawer) {
          this.cartAddonDrawer.hide();
        }

        const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
        cartJson['sections'] = parsedState['sections'];

        this.updateCartState(cartJson);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.submitEl.removeAttribute('aria-disabled');
        this.submitEl.classList.remove('btn--loading');
      });
  }

  updateCartState = (cartJson) => {
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
  };

  displayFormErrors = (errorMessage = false) => {
    if (!this.messageEl) {
      if (errorMessage !== false) {
        alert(errorMessage);
      }
    } else {
      this.messageEl.classList.toggle('!hidden', !errorMessage);
      if (errorMessage !== false) {
        this.messageEl.innerText = errorMessage;
      }
    }
  };
}
customElements.define('cart-discount', CartDiscount, { extends: 'form' });

class CartDiscountRemove extends HTMLButtonElement {
  constructor() {
    super();

    this.selectors = {
      list: '.discounts',
      item: '.discount',
    };

    this.clickHandler = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.listEl = this.closest(this.selectors.list);

    this.addEventListener('click', this.clickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.clickHandler);
  }

  handleClick(event) {
    event.preventDefault();
    if (this.getAttribute('aria-disabled') === 'true') return;

    this.setAttribute('aria-disabled', 'true');
    this.classList.add('btn--loading');

    this.discounts = [];

    const thisItem = this.closest('li');
    const items = this.listEl.querySelectorAll(this.selectors.item);
    items &&
      items.forEach((item) => {
        if (item != thisItem) {
          this.discounts.push(item.dataset.discountCode);
        }
      });

    this.updateCartDiscounts();
  }

  updateCartDiscounts() {
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const config = FoxTheme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);
    formData.append('discount', this.discounts.join(','));

    config.body = formData;

    fetch(FoxTheme.routes.cart_update_url, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
        cartJson['sections'] = parsedState['sections'];

        this.updateCartState(cartJson);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  updateCartState = (cartJson) => {
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
  };
}
customElements.define('cart-discount-remove', CartDiscountRemove, { extends: 'button' });

class CalculateShipping extends CartAddonModal {
  constructor() {
    super();
    this.countryProvince = this.querySelector('country-province');
    this.isCountrySetup = false;
  }
  static get observedAttributes() {
    return [...super.observedAttributes, 'data-show'];
  }
  show() {
    super.show();
    this.setAttribute('data-show', true);
  }

  hide() {
    super.hide();
    this.setAttribute('data-show', false);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === 'data-show' && newValue === 'true' && !this.isCountrySetup && this.countryProvince) {
      const template = this.countryProvince.querySelector('template');
      const templateContent = document.importNode(template.content, true);
      this.countryProvince.countryElement.appendChild(templateContent);
      this.countryProvince.init();
      this.isCountrySetup = true;
    }
  }
}
customElements.define('calculate-shipping', CalculateShipping);
class CountryProvinceForm extends HTMLElement {
  constructor() {
    super();
    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement = this.querySelector('[name="address[country]"]');
    this.template = this.dataset.template;
    this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));
    if (this.template && this.template === 'cart') {
      this.init();
    }
  }

  init() {
    if (this.getAttribute('country') !== '') {
      this.countryElement.selectedIndex = Math.max(
        0,
        Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.dataset.country)
      );
      this.countryElement.dispatchEvent(new Event('change'));
    } else {
      this.handleCountryChange();
    }
  }

  handleCountryChange() {
    const option = this.countryElement.options[this.countryElement.selectedIndex],
      provinces = JSON.parse(option.dataset.provinces);

    this.provinceElement.closest('.form-field').hidden = provinces.length === 0;

    if (provinces.length === 0) {
      return;
    }

    this.provinceElement.innerHTML = '';

    provinces.forEach((data) => {
      const selected = data[1] === this.dataset.province;
      this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
    });
  }
}
customElements.define('country-province', CountryProvinceForm);

class ShippingCalculator extends HTMLFormElement {
  constructor() {
    super();

    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.lastElementChild;

    this.submitButton.addEventListener('click', this.handleFormSubmit.bind(this));
  }

  handleFormSubmit(event) {
    event.preventDefault();

    const zip = this.querySelector('[name="address[zip]"]').value,
      country = this.querySelector('[name="address[country]"]').value,
      province = this.querySelector('[name="address[province]"]').value;

    this.submitButton.classList.add('btn--loading');

    const body = JSON.stringify({
      shipping_address: { zip, country, province },
    });
    let sectionUrl = `${FoxTheme.routes.cart_url}/shipping_rates.json`;

    sectionUrl = sectionUrl.replace('//', '/');

    fetch(sectionUrl, { ...FoxTheme.utils.fetchConfig('javascript'), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          this.formatShippingRates(parsedState.shipping_rates);
        } else {
          this.formatError(parsedState);
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.resultsElement.hidden = false;
        this.submitButton.classList.remove('btn--loading');
      });
  }

  formatError(errors) {
    const shippingRatesList = Object.keys(errors).map((errorKey) => {
      return `<li>${errors[errorKey]}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert alert--error blocks-radius grid gap-2">
        <p class="font-body-bolder m-0">${FoxTheme.shippingCalculatorStrings.error}</p>
        <ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join('')}</ul>
      </div>
    `;
  }

  formatShippingRates(shippingRates) {
    const shippingRatesList = shippingRates.map(({ presentment_name, currency, price }) => {
      return `<li>${presentment_name}: ${currency} ${price}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert blocks-radius alert--${
        shippingRates.length === 0 ? 'error' : 'success'
      } grid gap-2 leading-tight">
        <p class="font-body-bolder m-0">${
          shippingRates.length === 0
            ? FoxTheme.shippingCalculatorStrings.notFound
            : shippingRates.length === 1
            ? FoxTheme.shippingCalculatorStrings.oneResult
            : FoxTheme.shippingCalculatorStrings.multipleResults
        }</p>
        ${
          shippingRatesList === ''
            ? ''
            : `<ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join('')}</ul>`
        }
      </div>
    `;
  }
}
customElements.define('shipping-calculator', ShippingCalculator, { extends: 'form' });

class CartDrawerProductsRecommendation extends HTMLElement {
  constructor() {
    super();
    if ('requestIdleCallback' in window) {
      requestIdleCallback(this.init.bind(this), { timeout: 1500 });
    } else {
      const userAgent = navigator.userAgent;
      const isSafari = userAgent.includes('Safari') && !userAgent.includes('Chrome');
      if (isSafari) {
        this.init();
      } else {
        FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
      }
    }
  }

  get slideContainer() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  init() {
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((responseText) => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        if (sectionInnerHTML === null) return;

        const recommendations = sectionInnerHTML.querySelector('cart-drawer-products-recommendation');
        if (recommendations && recommendations.innerHTML.trim().length) {
          const productCount = recommendations.querySelectorAll('.product-card');
          this.innerHTML = recommendations.innerHTML;
          this.initCarousel();
          if (productCount.length > 0) this.classList.remove('hidden');
          this.dispatchEvent(new CustomEvent('recommendations:loaded'));
        } else {
          this.closest('.shopify-section').remove();
          this.classList.add('hidden');
          this.dispatchEvent(new CustomEvent('is-empty'));
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  initCarousel() {
    this.carousel = new FoxTheme.Carousel(this.slideContainer, {
      slidesPerView: 1,
      spaceBetween: 10,
      loop: false,
      pagination: {
        el: this.sliderPagination,
        type: 'fraction',
      },
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
    });

    this.carousel && this.carousel.init();
  }
}

customElements.define('cart-drawer-products-recommendation', CartDrawerProductsRecommendation);

class MainCart extends HTMLElement {
  constructor() {
    super();

    document.addEventListener('cart:grouped-sections', this.getSectionToRender.bind(this));
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }
}
customElements.define('main-cart', MainCart);

class FreeShippingGoal extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      leftToSpend: '[data-left-to-spend]',
    };
    this.goal = Number(this.dataset.minimumAmount) * Number(window.Shopify.currency.rate || 1) || 0;
    this.progress = this.querySelector('progress-bar');
    this.money_format = window.FoxTheme.settings.moneyFormat;
  }

  connectedCallback() {
    this.updateShippingGloal(Number(this.dataset.cartTotal));
    document.addEventListener('cart:updated', (event) => {
      this.updateShippingGloal(event.detail.cart.items_subtotal_price);
    });
  }

  updateShippingGloal(amount) {
    if (amount > 0) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }

    this.cartTotal = amount / 100;
    this.goalLeft = this.goal - this.cartTotal;
    this.goalDone = this.goalLeft <= 0;

    this.percent = (this.cartTotal * 100) / this.goal;

    if (this.percent >= 100) this.percent = 100;

    if (this.cartTotal >= this.goal) {
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.classList.add('free-shipping-goal--done');
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    } else {
      let spend = (this.goal - this.cartTotal) * 100;
      this.querySelector(this.selectors.leftToSpend).innerHTML = FoxTheme.Currency.formatMoney(
        spend,
        this.money_format
      );
      this.classList.remove('free-shipping-goal--done');
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    }
  }
}
customElements.define('free-shipping-goal', FreeShippingGoal);

class FreeGiftGoal extends HTMLElement {
  constructor() {
    super();
    this.goal = Number(this.dataset.minimumAmount) / 100 * Number(window.Shopify.currency.rate || 1) || 0;
    this.giftVariantId = this.dataset.giftVariantId;
    this.progress = this.querySelector('progress-bar');
    this.moneyFormat = window.FoxTheme.settings.moneyFormat;
    this.progressMessageTemplate = this.dataset.progressMessage;
    this.successMessage = this.dataset.successMessage;
    this.isUpdating = false;
    this.pendingUpdate = null;
    console.log('[FreeGiftGoal] constructed', {
      goal: this.goal,
      giftVariantId: this.giftVariantId,
      currency: window.Shopify?.currency,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      userAgent: navigator.userAgent,
    });
  }

  connectedCallback() {
    const initialTotal = Number(this.dataset.cartTotal);
    const initialGiftInCart = this.dataset.giftInCart === 'true';
    console.log('[FreeGiftGoal] connectedCallback (initial state from Liquid)', {
      cartTotal: initialTotal,
      cartTotalMajor: initialTotal / 100,
      giftInCart: initialGiftInCart,
      goal: this.goal,
    });
    this.updateDisplay(initialTotal);
    this.checkAndUpdateGift(initialTotal, initialGiftInCart);

    document.addEventListener('cart:updated', (event) => {
      const cart = event.detail.cart;
      const cartTotalWithoutGift = this.getCartTotalWithoutGift(cart);
      const giftInCart = this.isGiftInCart(cart);
      console.log('[FreeGiftGoal] cart:updated received', {
        cartTotal: cart.total_price,
        cartTotalWithoutGift,
        cartTotalWithoutGiftMajor: cartTotalWithoutGift / 100,
        giftInCart,
        goal: this.goal,
        items: cart.items.map((i) => ({
          title: i.title,
          variant_id: i.variant_id,
          qty: i.quantity,
          original: i.original_line_price,
          final: i.final_line_price,
          props: i.properties,
        })),
      });
      this.updateDisplay(cartTotalWithoutGift);
      this.checkAndUpdateGift(cartTotalWithoutGift, giftInCart);
    });
  }

  getCartTotalWithoutGift(cart) {
    let total = 0;
    for (const item of cart.items) {
      if (String(item.variant_id) === String(this.giftVariantId) && item.properties?._is_free_gift === 'true') continue;
      total += item.final_line_price;
    }
    return total;
  }

  isGiftInCart(cart) {
    return cart.items.some(
      (item) => String(item.variant_id) === String(this.giftVariantId) && item.properties?._is_free_gift === 'true'
    );
  }

  updateDisplay(cartTotal) {
    const cartTotalInMajor = cartTotal / 100;

    if (cartTotalInMajor > 0) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }

    const percent = Math.min((cartTotalInMajor * 100) / this.goal, 100);
    this.progress.style.setProperty('--percent', `${percent}%`);
    this.progress.dataset.value = cartTotalInMajor;
    this.progress.dataset.max = this.goal;

    if (cartTotalInMajor >= this.goal) {
      this.classList.add('free-shipping-goal--done');
    } else {
      this.classList.remove('free-shipping-goal--done');
      const remaining = (this.goal - cartTotalInMajor) * 100;
      const leftToSpendEl = this.querySelector('[data-gift-left-to-spend]');
      if (leftToSpendEl) {
        leftToSpendEl.innerHTML = FoxTheme.Currency.formatMoney(remaining, this.moneyFormat);
      }
    }
  }

  async checkAndUpdateGift(cartTotal, giftInCart) {
    const cartTotalInMajor = cartTotal / 100;
    const shouldHaveGift = cartTotalInMajor >= this.goal;
    const decision = shouldHaveGift && !giftInCart
      ? 'ADD'
      : !shouldHaveGift && giftInCart
        ? 'REMOVE'
        : 'NOOP';
    console.log('[FreeGiftGoal] checkAndUpdateGift', {
      cartTotalInMajor,
      goal: this.goal,
      shouldHaveGift,
      giftInCart,
      decision,
    });

    if (shouldHaveGift && !giftInCart) {
      await this.addGiftToCart();
    } else if (!shouldHaveGift && giftInCart) {
      await this.removeGiftFromCart();
    }
  }

  async addGiftToCart() {
    if (this.isUpdating) {
      console.log('[FreeGiftGoal] addGiftToCart SKIPPED – already updating, queued as pending', { pendingUpdate: 'add' });
      this.pendingUpdate = 'add';
      return;
    }
    this.isUpdating = true;
    console.time('[FreeGiftGoal] addGiftToCart');
    console.log('[FreeGiftGoal] addGiftToCart START', {
      giftVariantId: this.giftVariantId,
      url: FoxTheme.routes.cart_add_url,
    });

    try {
      const response = await fetch(FoxTheme.routes.cart_add_url, {
        ...FoxTheme.utils.fetchConfig(),
        body: JSON.stringify({
          items: [{ id: Number(this.giftVariantId), quantity: 1, properties: { _is_free_gift: 'true' } }],
        }),
      });

      console.log('[FreeGiftGoal] addGiftToCart fetch response', {
        ok: response.ok,
        status: response.status,
      });

      if (!response.ok) throw new Error('Failed to add gift');

      const responseJson = await response.clone().json().catch(() => null);
      console.log('[FreeGiftGoal] addGiftToCart response body', responseJson);

      await this.refreshCart();
    } catch (error) {
      console.error('[FreeGiftGoal] addGiftToCart ERROR', error);
    } finally {
      this.isUpdating = false;
      console.timeEnd('[FreeGiftGoal] addGiftToCart');
      this.processPendingUpdate();
    }
  }

  async removeGiftFromCart() {
    if (this.isUpdating) {
      console.log('[FreeGiftGoal] removeGiftFromCart SKIPPED – already updating, queued as pending', { pendingUpdate: 'remove' });
      this.pendingUpdate = 'remove';
      return;
    }
    this.isUpdating = true;
    console.time('[FreeGiftGoal] removeGiftFromCart');
    console.log('[FreeGiftGoal] removeGiftFromCart START', {
      giftVariantId: this.giftVariantId,
    });

    try {
      const cartResponse = await fetch(FoxTheme.routes.cart_url, {
        ...FoxTheme.utils.fetchConfig(),
      });
      const cart = await cartResponse.json();

      const giftItem = cart.items.find(
        (item) => String(item.variant_id) === String(this.giftVariantId) && item.properties?._is_free_gift === 'true'
      );
      if (!giftItem) {
        console.log('[FreeGiftGoal] removeGiftFromCart – gift not found in cart, nothing to do');
        return;
      }

      console.log('[FreeGiftGoal] removeGiftFromCart – changing qty to 0', {
        key: giftItem.key,
        currentQty: giftItem.quantity,
      });

      const response = await fetch(FoxTheme.routes.cart_change_url, {
        ...FoxTheme.utils.fetchConfig(),
        body: JSON.stringify({
          id: giftItem.key,
          quantity: 0,
        }),
      });

      console.log('[FreeGiftGoal] removeGiftFromCart fetch response', {
        ok: response.ok,
        status: response.status,
      });

      if (!response.ok) throw new Error('Failed to remove gift');

      await this.refreshCart();
    } catch (error) {
      console.error('[FreeGiftGoal] removeGiftFromCart ERROR', error);
    } finally {
      this.isUpdating = false;
      console.timeEnd('[FreeGiftGoal] removeGiftFromCart');
      this.processPendingUpdate();
    }
  }

  async refreshCart() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
  }

  processPendingUpdate() {
    if (CartProgressGoals.pendingUpdate) {
      const action = CartProgressGoals.pendingUpdate;
      CartProgressGoals.pendingUpdate = null;
      if (action === 'add') this.addGiftToCart();
      else if (action === 'remove') this.removeGiftFromCart();
    }
  }
}
customElements.define('free-gift-goal', FreeGiftGoal);

class CartProgressGoals extends HTMLElement {
  // Static flags survive element re-creation (cart:refresh re-renders the drawer)
  static isUpdating = false;
  static pendingUpdate = null;
  static addFailed = false;
  static lastKnownTotal = null;
  static lastGiftInCart = null;
  static userRemovedGift = false;

  constructor() {
    super();
    const rate = Number(window.Shopify.currency.rate || 1);
    this.shippingMinimum = (Number(this.dataset.shippingMinimum) / 100) * rate || 0;
    this.giftMinimum = (Number(this.dataset.giftMinimum) / 100) * rate || 0;
    this.giftVariantId = this.dataset.giftVariantId || '';
    this.giftProgressMessage = this.dataset.giftProgressMessage || '';
    this.giftSuccessMessage = this.dataset.giftSuccessMessage || '';
    this.progress = this.querySelector('progress-bar');
    this.messageEl = this.querySelector('[data-progress-message]');
    this.moneyFormat = window.FoxTheme.settings.moneyFormat;

    this.barMax = Math.max(this.shippingMinimum, this.giftMinimum);
    this.milestones = this.buildMilestones();

    this.onCartUpdated = this.onCartUpdated.bind(this);
  }

  buildMilestones() {
    const milestones = [];
    if (this.giftMinimum > 0) {
      milestones.push({ type: 'gift', amount: this.giftMinimum });
    }
    if (this.shippingMinimum > 0) {
      milestones.push({ type: 'shipping', amount: this.shippingMinimum });
    }
    milestones.sort((a, b) => a.amount - b.amount);
    return milestones;
  }

  connectedCallback() {
    const initialTotal = Number(this.dataset.cartTotal) / 100;
    const giftInCart = this.dataset.giftInCart === 'true';
    this.updateDisplay(initialTotal);

    // Reset failure flag if cart total changed
    if (CartProgressGoals.addFailed && CartProgressGoals.lastKnownTotal !== null && Math.abs(initialTotal - CartProgressGoals.lastKnownTotal) > 0.01) {
      CartProgressGoals.addFailed = false;
    }
    // Reset userRemovedGift if total increased (user added a new product)
    if (CartProgressGoals.userRemovedGift && CartProgressGoals.lastKnownTotal !== null && initialTotal > CartProgressGoals.lastKnownTotal + 0.01) {
      CartProgressGoals.userRemovedGift = false;
    }

    CartProgressGoals.lastKnownTotal = initialTotal;
    CartProgressGoals.lastGiftInCart = giftInCart;

    if (this.giftVariantId && this.giftMinimum > 0 && !CartProgressGoals.addFailed) {
      this.checkAndUpdateGift(initialTotal, giftInCart);
    }

    document.addEventListener('cart:updated', this.onCartUpdated);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.onCartUpdated);
  }

  onCartUpdated(event) {
    const cart = event.detail.cart;
    if (!cart || !cart.items) return;
    const cartTotal = this.getCartTotalWithoutGift(cart) / 100;
    const giftInCart = this.isGiftInCart(cart);
    this.updateDisplay(cartTotal);

    const totalIncreased = CartProgressGoals.lastKnownTotal !== null && cartTotal > CartProgressGoals.lastKnownTotal + 0.01;

    // Reset flags if total increased (user added a new product)
    if (totalIncreased) {
      CartProgressGoals.userRemovedGift = false;
      CartProgressGoals.addFailed = false;
    } else if (CartProgressGoals.addFailed && CartProgressGoals.lastKnownTotal !== null && Math.abs(cartTotal - CartProgressGoals.lastKnownTotal) > 0.01) {
      CartProgressGoals.addFailed = false;
    }

    // Detect manual removal: gift was present, now gone, total still above threshold, and total didn't increase
    if (!totalIncreased && CartProgressGoals.lastGiftInCart === true && !giftInCart && cartTotal >= this.giftMinimum) {
      CartProgressGoals.userRemovedGift = true;
    }

    CartProgressGoals.lastKnownTotal = cartTotal;
    CartProgressGoals.lastGiftInCart = giftInCart;

    if (this.giftVariantId && this.giftMinimum > 0 && !CartProgressGoals.addFailed) {
      this.checkAndUpdateGift(cartTotal, giftInCart);
    }
  }

  getCartTotalWithoutGift(cart) {
    if (!this.giftVariantId) return cart.items_subtotal_price;
    let total = 0;
    for (const item of cart.items) {
      if (String(item.variant_id) === String(this.giftVariantId) && item.properties?._is_free_gift === 'true') continue;
      total += item.final_line_price;
    }
    return total;
  }

  isGiftInCart(cart) {
    return cart.items.some(
      (item) => String(item.variant_id) === String(this.giftVariantId) && item.properties?._is_free_gift === 'true'
    );
  }

  updateDisplay(cartTotal) {
    if (cartTotal > 0) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }

    const percent = this.barMax > 0 ? Math.min((cartTotal / this.barMax) * 100, 100) : 0;
    this.progress.style.setProperty('--percent', `${percent}%`);

    this.querySelectorAll('.cart-progress-goals__marker').forEach((marker) => {
      const markerAmount = Number(marker.dataset.milestoneAmount) / 100;
      marker.classList.toggle('cart-progress-goals__marker--reached', cartTotal >= markerAmount);
    });

    this.updateMessage(cartTotal);
  }

  updateMessage(cartTotal) {
    if (!this.messageEl) return;

    const nextMilestone = this.milestones.find((m) => cartTotal < m.amount);
    const allReached = !nextMilestone;

    if (allReached) {
      const lastMilestone = this.milestones[this.milestones.length - 1];
      if (lastMilestone.type === 'shipping') {
        this.messageEl.innerHTML = this.getShippingSuccessMessage();
      } else {
        this.messageEl.innerHTML = this.giftSuccessMessage;
      }
      this.classList.add('cart-progress-goals--all-done');
    } else {
      this.classList.remove('cart-progress-goals--all-done');
      const remaining = (nextMilestone.amount - cartTotal) * 100;
      const formattedRemaining = FoxTheme.Currency.formatMoney(remaining, this.moneyFormat);

      const reached = this.milestones.filter((m) => cartTotal >= m.amount);

      if (nextMilestone.type === 'shipping') {
        const remainingHtml = `<strong>${formattedRemaining}</strong>`;
        if (reached.some((m) => m.type === 'gift')) {
          this.messageEl.innerHTML = `${this.giftSuccessMessage} Noch ${remainingHtml} bis zum kostenlosen Versand!`;
        } else {
          this.messageEl.innerHTML = `Noch ${remainingHtml} bis zum kostenlosen Versand!`;
        }
      } else {
        const remainingHtml = `<strong data-gift-left-to-spend>${formattedRemaining}</strong>`;
        if (reached.some((m) => m.type === 'shipping')) {
          this.messageEl.innerHTML = `${this.getShippingSuccessMessage()} ${this.giftProgressMessage.replace('{{ remaining_amount }}', remainingHtml)}`;
        } else {
          this.messageEl.innerHTML = this.giftProgressMessage.replace('{{ remaining_amount }}', remainingHtml);
        }
      }
    }
  }

  getShippingSuccessMessage() {
    const el = document.querySelector('[data-shipping-success-text]');
    if (el) return el.textContent;
    return 'Kostenloser Versand freigeschaltet!';
  }

  async checkAndUpdateGift(cartTotal, giftInCart) {
    const shouldHaveGift = cartTotal >= this.giftMinimum;
    if (shouldHaveGift && !giftInCart && !CartProgressGoals.userRemovedGift) {
      await this.addGiftToCart();
    } else if (!shouldHaveGift && giftInCart) {
      CartProgressGoals.userRemovedGift = false; // Threshold no longer met, reset so gift can be re-added if total rises again
      await this.removeGiftFromCart();
    }
  }

  async addGiftToCart() {
    if (CartProgressGoals.isUpdating || CartProgressGoals.addFailed) {
      if (!CartProgressGoals.addFailed) CartProgressGoals.pendingUpdate = 'add';
      return;
    }
    CartProgressGoals.isUpdating = true;
    try {
      const response = await fetch(FoxTheme.routes.cart_add_url, {
        ...FoxTheme.utils.fetchConfig('javascript'),
        body: JSON.stringify({
          items: [{ id: Number(this.giftVariantId), quantity: 1, properties: { _is_free_gift: 'true' } }],
        }),
      });
      const data = await response.json();
      if (data.status) {
        console.error('CartProgressGoals: failed to add gift –', data.message, data.description);
        CartProgressGoals.addFailed = true;
        CartProgressGoals.pendingUpdate = null;
        return;
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
    } catch (error) {
      console.error('CartProgressGoals: error adding gift', error);
      CartProgressGoals.addFailed = true;
      CartProgressGoals.pendingUpdate = null;
    } finally {
      CartProgressGoals.isUpdating = true;
      setTimeout(() => {
        CartProgressGoals.isUpdating = false;
        this.processPendingUpdate();
      }, 1000);
    }
  }

  async removeGiftFromCart() {
    if (CartProgressGoals.isUpdating) {
      CartProgressGoals.pendingUpdate = 'remove';
      return;
    }
    CartProgressGoals.isUpdating = true;
    try {
      const cartResponse = await fetch(FoxTheme.routes.cart_url, {
        ...FoxTheme.utils.fetchConfig(),
      });
      const cart = await cartResponse.json();
      const giftItem = cart.items.find(
        (item) => String(item.variant_id) === String(this.giftVariantId) && item.properties?._is_free_gift === 'true'
      );
      if (!giftItem) return;
      const response = await fetch(FoxTheme.routes.cart_change_url, {
        ...FoxTheme.utils.fetchConfig(),
        body: JSON.stringify({ id: giftItem.key, quantity: 0 }),
      });
      const data = await response.json();
      if (data.errors) {
        console.error('CartProgressGoals: failed to remove gift –', data.errors);
        return;
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
    } catch (error) {
      console.error('CartProgressGoals: error removing gift', error);
    } finally {
      CartProgressGoals.isUpdating = true;
      setTimeout(() => {
        CartProgressGoals.isUpdating = false;
        this.processPendingUpdate();
      }, 1000);
    }
  }

  processPendingUpdate() {
    if (CartProgressGoals.pendingUpdate) {
      const action = CartProgressGoals.pendingUpdate;
      CartProgressGoals.pendingUpdate = null;
      if (action === 'add') this.addGiftToCart();
      else if (action === 'remove') this.removeGiftFromCart();
    }
  }
}
customElements.define('cart-progress-goals', CartProgressGoals);

window.FoxKitAddToCart = async (payload) => {
  if (!payload?.properties?.['_FoxKit offer']) return;

  const cartJson = await (
    await fetch(`${FoxTheme.routes.cart_url}`, {
      ...FoxTheme.utils.fetchConfig(),
    })
  ).json();
  cartJson['sections'] = payload['sections'];
  FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });

  document.dispatchEvent(
    new CustomEvent('product-ajax:added', {
      detail: {
        product: payload,
      },
    })
  );
};
